# Base modal wrapper component
<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    @click="handleBackdropClick"
  >
    <!-- Upload Modal -->
    <div
      v-if="modalType === 'upload'"
      class="flex flex-col gap-4 bg-neutral-800 rounded-lg w-full max-w-lg p-4"
      @click.stop
    >
      <div class="flex justify-between items-center">
        <h2 class="text-xl font-bold text-white">Upload New Photos</h2>
        <Button
          @click="closeModal"
          variant="transparent"
          icon="close"
          size="sm"
          class="text-gray-400 hover:text-white"
        />
      </div>

      <!-- Date and Tag Selection -->
      <div class="grid grid-cols-1 gap-4">
        <!-- Date Selection -->
        <div class="flex flex-col gap-2">
          <label class="block text-gray-400 text-sm">Date</label>
          <input
            type="date"
            v-model="uploadDate"
            :disabled="isAnyFileUploading"
            class="w-full bg-neutral-700 border border-neutral-600 rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#27bdf4] disabled:opacity-50 disabled:cursor-not-allowed"
            :class="{ 'border-red-500': dateError }"
            :min="'2017-01-01'"
            :max="new Date().toISOString().split('T')[0]"
          />
          <p v-if="dateError" class="text-red-500 text-sm mt-1">
            {{ dateError }}
          </p>
        </div>

        <!-- Tag Selection -->
        <div class="flex flex-col gap-2">
          <label class="block text-gray-400 text-sm">Tag</label>
          <div v-if="!isCreatingNewTag" class="flex gap-2 flex-1">
            <select
              v-model="selectedTag"
              :disabled="!tags.length || isAnyFileUploading"
              class="flex-1 bg-neutral-700 border border-neutral-600 rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#27bdf4] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="" disabled selected>Select a tag</option>
              <option v-for="tag in tags" :key="tag.id" :value="tag.id">
                {{ tag.label }}
              </option>
            </select>
            <Button
              @click="startNewTagCreation"
              variant="neutral"
              size="md"
              :disabled="isAnyFileUploading"
              class="whitespace-nowrap"
            >
              New Tag
            </Button>
            <Button
              v-if="selectedTag"
              @click="confirmDeleteTag"
              variant="danger"
              size="md"
              :disabled="isAnyFileUploading"
              class="whitespace-nowrap"
            >
              Delete Tag
            </Button>

            <p v-if="!tags.length" class="text-gray-500 text-sm mt-1">
              No tags available
            </p>
          </div>
          <div v-else class="space-y-2 flex-1">
            <div class="flex gap-2">
              <input
                v-model="newTagName"
                type="text"
                :disabled="isAnyFileUploading"
                placeholder="Enter new tag name"
                class="flex-1 bg-neutral-700 border border-neutral-600 rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#27bdf4] disabled:opacity-50 disabled:cursor-not-allowed"
                @keyup.enter="createNewTag"
              />
              <Button
                @click="createNewTag"
                variant="primary"
                size="md"
                :disabled="!newTagName.trim() || isAnyFileUploading"
              >
                Create
              </Button>
              <Button
                @click="cancelNewTagCreation"
                variant="neutral"
                size="md"
                :disabled="isAnyFileUploading"
              >
                Cancel
              </Button>
            </div>
            <p v-if="tagError" class="text-red-500 text-sm">
              {{ tagError }}
            </p>
          </div>
        </div>

        <!-- Add new tag button -->
      </div>

      <!-- Upload Area -->
      <div
        class="border-2 border-dashed rounded-lg p-8 transition-colors duration-300"
        :class="[
          uploadError
            ? 'border-red-500 bg-red-500/5'
            : isAnyFileUploading
            ? 'border-neutral-600 opacity-50 cursor-not-allowed'
            : selectedTag
            ? 'border-neutral-600 hover:border-neutral-500 cursor-pointer'
            : 'border-neutral-600 opacity-50',
        ]"
        @drop.prevent="handleDrop"
        @dragover.prevent
        @dragenter.prevent
      >
        <div class="text-center">
          <div v-if="!selectedTag" class="text-gray-500">
            Please select a tag first
          </div>
          <div v-else-if="uploadError" class="space-y-2">
            <div class="flex items-center justify-center text-red-500">
              <i class="mdi mdi-alert-circle text-xl mr-2"></i>
              <p>Only one image allowed at a time</p>
            </div>
            <p class="text-gray-500 text-sm">
              Remove the current image before uploading a new one
            </p>
          </div>
          <div v-else>
            <p class="text-gray-400">
              {{
                isAnyFileUploading
                  ? "Upload in progress..."
                  : "Drag and Drop a single image here"
              }}
            </p>

            <Button
              @click="$refs.fileInput.click()"
              variant="neutral"
              size="md"
              :disabled="isAnyFileUploading"
              class="mt-2"
            >
              Choose Image
            </Button>
            <input
              type="file"
              ref="fileInput"
              class="hidden"
              accept="image/*"
              :disabled="isAnyFileUploading"
              @change="handleFileSelect"
            />
          </div>
        </div>
      </div>

      <!-- Selected Files Preview -->
      <div v-if="hasSelectedFiles" class="mb-6">
        <h3 class="text-white text-lg mb-3">Selected Files</h3>
        <div class="space-y-2 max-h-60 overflow-y-auto pr-2">
          <div
            v-for="(fileKey, index) in Object.keys(selectedFiles)"
            :key="fileKey"
            class="flex items-center bg-neutral-900 rounded-md p-2"
          >
            <img
              :src="selectedFilePreviews[fileKey]"
              class="w-12 h-12 object-cover rounded mr-3"
            />
            <div class="flex-1">
              <div class="text-white text-sm">
                {{ selectedFiles[fileKey].name }}
              </div>
              <div class="text-gray-400 text-xs">
                {{ formatFileSize(selectedFiles[fileKey].size) }}
              </div>
              <div v-if="isUploading[fileKey]" class="mt-2">
                <div class="bg-neutral-700 rounded-full h-1.5 overflow-hidden">
                  <div
                    class="h-full bg-[#27bdf4] transition-all duration-300"
                    :style="{ width: `${uploadProgress[fileKey]}%` }"
                  ></div>
                </div>
                <div class="text-xs text-gray-400 mt-1">
                  Progress: {{ Math.round(uploadProgress[fileKey]) }}%
                </div>
              </div>
            </div>
            <Button
              v-if="!isUploading[fileKey]"
              @click="confirmRemoveFile(fileKey)"
              variant="transparent"
              icon="close"
              size="sm"
              class="text-gray-400 hover:text-white"
            />
          </div>
        </div>
      </div>

      <!-- Simple Confirmation Modal -->
      <div
        v-if="showConfirmModal"
        class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]"
        @click="cancelConfirmation"
      >
        <div class="bg-neutral-800 rounded-lg w-full max-w-md p-6" @click.stop>
          <h3 class="text-xl font-bold text-white mb-4">{{ confirmTitle }}</h3>
          <p class="text-gray-300 mb-6">{{ confirmMessage }}</p>
          <div class="flex justify-end space-x-3">
            <Button @click="cancelConfirmation" variant="neutral" size="md">
              Cancel
            </Button>
            <Button
              @click="handleConfirmation"
              :variant="confirmVariant"
              size="md"
            >
              {{ confirmActionText }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Delete Tag Confirmation Modal -->
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]"
        @click="cancelDeleteTag"
      >
        <div class="bg-neutral-800 rounded-lg w-full max-w-md p-6" @click.stop>
          <h3 class="text-xl font-bold text-white mb-4">Delete Tag</h3>
          <p class="text-gray-300 mb-4">
            This action will permanently delete the tag "{{ deleteTagLabel }}"
            and all associated images. This cannot be undone.
          </p>
          <p class="text-gray-300 mb-4">
            Type
            <span class="font-mono bg-neutral-700 px-2 py-1 rounded"
              >DELETE</span
            >
            to confirm:
          </p>
          <input
            v-model="deleteConfirmation"
            type="text"
            class="w-full bg-neutral-700 border border-neutral-600 rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#27bdf4] mb-4"
            placeholder="Type DELETE to confirm"
          />
          <div class="flex justify-end space-x-3">
            <Button @click="cancelDeleteTag" variant="neutral" size="md">
              Cancel
            </Button>
            <Button
              @click="executeDeleteTag"
              variant="danger"
              size="md"
              :disabled="deleteConfirmation !== 'DELETE'"
            >
              Delete Tag
            </Button>
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="flex justify-end space-x-3">
        <Button
          @click="closeModal"
          variant="neutral"
          :disabled="isAnyFileUploading"
        >
          Cancel
        </Button>
        <Button
          @click="uploadImages"
          variant="primary"
          icon="cloud-upload"
          :disabled="!canUpload"
        >
          Upload
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    getStorage,
    ref as storageRef,
    uploadBytesResumable,
    getDownloadURL,
  } from "firebase/storage";
  import Button from "../ui/Button.vue";
  import {
    uploadImageToGCS,
    validateTag,
    formatTagForStorage,
  } from "./services/GoogleCloudBucket";

  // Modal state management service
  class ModalManager {
    constructor() {
      this.activeModal = null;
      this.modalData = null;
    }

    openModal(modalType, data = null) {
      this.activeModal = modalType;
      this.modalData = data;
    }

    closeModal() {
      this.activeModal = null;
      this.modalData = null;
    }

    getActiveModal() {
      return this.activeModal;
    }

    getModalData() {
      return this.modalData;
    }

    isModalOpen(modalType) {
      return this.activeModal === modalType;
    }
  }

  // Modal types enum
  const MODAL_TYPES = {
    UPLOAD: "upload",
    DELETE: "delete",
    TIMELAPSE: "timelapse",
  };

  // Upload modal state management
  class UploadModalManager {
    constructor() {
      this.selectedFiles = {};
      this.selectedFilePreviews = {};
      this.uploadProgress = {};
      this.isUploading = {};
      this.uploadError = false;
      this.uploadErrorTimeout = null;
    }

    clearState() {
      // Revoke all existing object URLs
      Object.values(this.selectedFilePreviews).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });

      this.selectedFiles = {};
      this.selectedFilePreviews = {};
      this.uploadProgress = {};
      this.isUploading = {};
      this.uploadError = false;

      if (this.uploadErrorTimeout) {
        clearTimeout(this.uploadErrorTimeout);
        this.uploadErrorTimeout = null;
      }
    }

    setUploadProgress(fileKey, progress) {
      console.log("UploadManager setUploadProgress:", { fileKey, progress });
      // Create a new object to ensure reactivity
      this.uploadProgress = {
        ...this.uploadProgress,
        [fileKey]: progress,
      };
    }

    setIsUploading(fileKey, isUploading) {
      console.log("UploadManager setIsUploading:", { fileKey, isUploading });
      // Create a new object to ensure reactivity
      this.isUploading = {
        ...this.isUploading,
        [fileKey]: isUploading,
      };
    }

    getUploadProgress() {
      return this.uploadProgress;
    }

    getIsUploading() {
      return this.isUploading;
    }

    handleFileSelection(file) {
      if (!file.type.startsWith("image/")) {
        throw new Error("Please select an image file.");
      }

      // Clear any existing files first
      this.clearState();

      // Create a unique key for the file using name and timestamp
      const fileKey = `${file.name}-${Date.now()}`;

      // Create object URL for preview
      const previewUrl = URL.createObjectURL(file);

      this.selectedFilePreviews = { [fileKey]: previewUrl };
      this.selectedFiles = { [fileKey]: file };
      this.uploadProgress = { [fileKey]: 0 };
      this.isUploading = { [fileKey]: false };

      return fileKey;
    }

    removeFile(fileKey) {
      if (this.selectedFilePreviews[fileKey]) {
        URL.revokeObjectURL(this.selectedFilePreviews[fileKey]);
      }

      const { [fileKey]: _, ...remainingFiles } = this.selectedFiles;
      const { [fileKey]: __, ...remainingPreviews } = this.selectedFilePreviews;
      const { [fileKey]: ___, ...remainingProgress } = this.uploadProgress;
      const { [fileKey]: ____, ...remainingUploading } = this.isUploading;

      this.selectedFiles = remainingFiles;
      this.selectedFilePreviews = remainingPreviews;
      this.uploadProgress = remainingProgress;
      this.isUploading = remainingUploading;
    }

    getSelectedFiles() {
      return this.selectedFiles;
    }

    getSelectedFilePreviews() {
      return this.selectedFilePreviews;
    }

    hasSelectedFiles() {
      return Object.keys(this.selectedFiles).length > 0;
    }

    setUploadError(error) {
      this.uploadError = error;

      if (this.uploadErrorTimeout) {
        clearTimeout(this.uploadErrorTimeout);
      }

      this.uploadErrorTimeout = setTimeout(() => {
        this.uploadError = false;
      }, 3000);
    }

    getUploadError() {
      return this.uploadError;
    }
  }

  export default {
    name: "UploadModal",
    components: {
      Button,
    },
    props: {
      isOpen: {
        type: Boolean,
        required: true,
      },
      modalType: {
        type: String,
        required: true,
        validator: (value) => Object.values(MODAL_TYPES).includes(value),
      },
      branchId: {
        type: String,
        required: true,
      },
      nurseryId: {
        type: String,
        required: true,
      },
      tags: {
        type: Array,
        required: true,
      },
      activeTag: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        modalManager: new ModalManager(),
        uploadManager: new UploadModalManager(),
        selectedTag: "",
        uploadDate: new Date().toISOString().split("T")[0],
        isCreatingNewTag: false,
        newTagName: "",
        tagError: null,
        dateError: null,
        showDeleteModal: false,
        deleteConfirmation: "",
        deleteTagLabel: "",
        showConfirmModal: false,
        confirmTitle: "",
        confirmMessage: "",
        confirmActionText: "",
        confirmVariant: "danger",
        confirmCallback: null,
        fileKeyToRemove: null,
      };
    },
    computed: {
      availableYears() {
        const currentYear = new Date().getFullYear();
        const startYear = 2017;
        const years = [];
        for (let year = startYear; year <= currentYear; year++) {
          years.push(year);
        }
        return years;
      },
      availableDays() {
        const daysInMonth = new Date(
          new Date(this.uploadDate).getFullYear(),
          new Date(this.uploadDate).getMonth(),
          0
        ).getDate();
        return Array.from({ length: daysInMonth }, (_, i) => i + 1);
      },
      uploadDateParts() {
        return {
          year: new Date(this.uploadDate).getFullYear(),
          month: new Date(this.uploadDate).getMonth() + 1,
          day: new Date(this.uploadDate).getDate(),
        };
      },
      selectedFiles() {
        return this.uploadManager.getSelectedFiles();
      },
      selectedFilePreviews() {
        return this.uploadManager.getSelectedFilePreviews();
      },
      uploadProgress() {
        console.log(
          "uploadProgress computed property called:",
          this.uploadManager.getUploadProgress()
        );
        return this.uploadManager.getUploadProgress();
      },
      isUploading() {
        console.log(
          "isUploading computed property called:",
          this.uploadManager.getIsUploading()
        );
        return this.uploadManager.getIsUploading();
      },
      uploadError() {
        return this.uploadManager.getUploadError();
      },
      hasSelectedFiles() {
        return this.uploadManager.hasSelectedFiles();
      },
      isAnyFileUploading() {
        return Object.values(this.isUploading).some((val) => val);
      },
      canUpload() {
        return (
          this.selectedTag && this.hasSelectedFiles && !this.isAnyFileUploading
        );
      },
    },
    watch: {
      isOpen(newVal) {
        if (newVal) {
          // When modal opens, set the selected tag to the active tag if available
          this.selectedTag = this.activeTag || "";
        }
      },
      uploadProgress: {
        deep: true,
        handler(newVal) {
          console.log("Upload progress changed:", newVal);
        },
      },
    },
    methods: {
      handleBackdropClick() {
        if (!this.isAnyFileUploading) {
          this.closeModal();
        }
      },
      closeModal() {
        this.uploadManager.clearState();
        this.selectedTag = "";
        this.isCreatingNewTag = false;
        this.newTagName = "";
        this.tagError = null;
        this.dateError = null;
        this.$emit("close");
      },
      handleDrop(event) {
        event.preventDefault();

        // If uploading or no tag selected, ignore drop
        if (this.isAnyFileUploading || !this.selectedTag) {
          return;
        }

        const files = Array.from(event.dataTransfer.files);
        const imageFile = files.find((file) => file.type.startsWith("image/"));

        if (imageFile) {
          if (this.hasSelectedFiles) {
            this.uploadManager.setUploadError(true);
            return;
          }
          this.handleFileSelection(imageFile);
        }
      },
      handleFileSelect(event) {
        if (!this.selectedTag) return;

        const files = Array.from(event.target.files);
        const imageFile = files.find((file) => file.type.startsWith("image/"));

        if (imageFile) {
          if (this.hasSelectedFiles) {
            this.uploadManager.setUploadError(true);
            return;
          }
          this.handleFileSelection(imageFile);
        }
        event.target.value = "";
      },
      handleFileSelection(file) {
        try {
          this.uploadManager.handleFileSelection(file);
        } catch (error) {
          this.$emit("error", error.message);
        }
      },
      removeFile(fileKey) {
        this.uploadManager.removeFile(fileKey);
      },
      formatFileSize(bytes) {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
      },
      async uploadImages() {
        if (!this.canUpload) return;

        // Clear any previous errors
        this.dateError = null;

        const uploads = [];
        const timestamp = new Date(this.uploadDate).getTime();

        for (const [fileKey, file] of Object.entries(this.selectedFiles)) {
          this.uploadManager.setIsUploading(fileKey, true);
          this.uploadManager.setUploadProgress(fileKey, 0);

          const upload = this.uploadSingleImage(file, fileKey, timestamp);
          uploads.push(upload);
        }

        try {
          await Promise.all(uploads);
          this.$emit("upload-success");
          this.$emit("refresh");
          this.closeModal();
        } catch (error) {
          console.error("Error uploading images:", error);

          // Handle duplicate date error specifically
          if (error.message?.includes("image already exists for this date")) {
            this.dateError =
              "An image already exists for this date. Please choose a different date.";
            // Reset upload state
            for (const fileKey of Object.keys(this.selectedFiles)) {
              this.uploadManager.setIsUploading(fileKey, false);
              this.uploadManager.setUploadProgress(fileKey, 0);
            }
          } else {
            this.$emit("error", "Failed to upload images. Please try again.");
          }
        }
      },
      async uploadSingleImage(file, fileKey, timestamp) {
        try {
          console.log(
            "Starting upload in modal for file:",
            file.name,
            "with key:",
            fileKey,
            "timestamp:",
            timestamp
          );

          // Find the selected tag object
          const selectedTagObj = this.tags.find(
            (tag) => tag.id === this.selectedTag
          );
          if (!selectedTagObj) {
            throw new Error("Selected tag not found");
          }

          // Use the storage-safe format for the file path
          const storageTag = selectedTagObj.storageId;

          // Initialize upload state
          this.uploadManager.setIsUploading(fileKey, true);
          this.uploadManager.setUploadProgress(fileKey, 0);

          // Upload using the service with storage-safe tag
          const uploadedImage = await uploadImageToGCS(
            this.branchId,
            this.nurseryId,
            storageTag,
            file,
            (progress) => {
              console.log("Progress update received in modal:", {
                fileKey,
                progress,
              });

              if (progress.state === "error") {
                console.error("Upload error in modal:", progress.error);
                this.uploadManager.setIsUploading(fileKey, false);
                this.$emit(
                  "error",
                  "Failed to upload image. Please try again."
                );
                return;
              }

              // Update progress
              this.uploadManager.setUploadProgress(
                fileKey,
                progress.totalProgress
              );
              console.log("Progress updated in modal:", {
                fileKey,
                newProgress: this.uploadManager.getUploadProgress()[fileKey],
              });

              // Handle completion
              if (progress.state === "complete") {
                console.log("Upload complete in modal for:", fileKey);
                this.uploadManager.setIsUploading(fileKey, false);
              }
            },
            timestamp
          );

          // Emit success with the complete image data including original tag format
          this.$emit("image-uploaded", {
            ...uploadedImage,
            tag: this.selectedTag, // Use original tag format
          });
        } catch (error) {
          console.error(`Error in modal uploadSingleImage:`, error);
          this.uploadManager.setIsUploading(fileKey, false);
          this.$emit("error", "Failed to upload image. Please try again.");
          throw error;
        }
      },
      startNewTagCreation() {
        this.isCreatingNewTag = true;
        this.newTagName = "";
        this.tagError = null;
      },
      cancelNewTagCreation() {
        this.isCreatingNewTag = false;
        this.newTagName = "";
        this.tagError = null;
      },
      async createNewTag() {
        const tagName = this.newTagName.trim();
        if (!tagName) {
          this.tagError = "Tag name cannot be empty";
          return;
        }

        // Validate tag format
        if (!validateTag(tagName)) {
          this.tagError =
            'Tag cannot contain special characters: / \\ ? % * : | " < >';
          return;
        }

        // Create the new tag preserving original format
        const newTag = {
          id: tagName, // Original format
          label: tagName, // Original format
          storageId: formatTagForStorage(tagName), // Storage-safe format
        };

        // Update the local tags array
        this.$emit("new-tag", newTag);

        // Select the new tag
        this.selectedTag = newTag.id;

        // Reset the new tag interface
        this.isCreatingNewTag = false;
        this.newTagName = "";
        this.tagError = null;
      },
      confirmDeleteTag() {
        const tagToDelete = this.tags.find(
          (tag) => tag.id === this.selectedTag
        );
        if (!tagToDelete) return;

        this.deleteTagLabel = tagToDelete.label;
        this.deleteConfirmation = "";
        this.showDeleteModal = true;
      },
      cancelDeleteTag() {
        this.showDeleteModal = false;
        this.deleteConfirmation = "";
        this.deleteTagLabel = "";
      },
      async executeDeleteTag() {
        if (this.deleteConfirmation !== "DELETE") return;

        try {
          // Emit delete-tag event to parent component
          this.$emit("delete-tag", this.selectedTag);

          // Reset states
          this.selectedTag = "";
          this.cancelDeleteTag();
        } catch (error) {
          this.$emit("error", "Failed to delete tag. Please try again.");
        }
      },
      confirmRemoveFile(fileKey) {
        this.fileKeyToRemove = fileKey;
        this.showConfirmation(
          "Remove Image",
          "Are you sure you want to remove this image?",
          "Remove",
          "danger",
          () => {
            this.removeFile(this.fileKeyToRemove);
            this.fileKeyToRemove = null;
          }
        );
      },
      showConfirmation(title, message, actionText, variant, callback) {
        this.confirmTitle = title;
        this.confirmMessage = message;
        this.confirmActionText = actionText;
        this.confirmVariant = variant;
        this.confirmCallback = callback;
        this.showConfirmModal = true;
      },
      cancelConfirmation() {
        this.showConfirmModal = false;
        this.confirmCallback = null;
        this.fileKeyToRemove = null;
      },
      handleConfirmation() {
        if (this.confirmCallback) {
          this.confirmCallback();
        }
        this.cancelConfirmation();
      },
    },
  };
</script>

<style scoped></style>
